import { createContext, useContext, useReducer, useCallback } from 'react';

const CartContext = createContext(null);

const initialState = {
  items: [],
  isOpen: false,
  selectedLocation: null,
  tip: 0,
  promoCode: null,
  promoDiscount: 0,
};

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingIndex = state.items.findIndex(
        item => item.id === action.payload.id && 
        JSON.stringify(item.selectedVariants) === JSON.stringify(action.payload.selectedVariants) &&
        JSON.stringify(item.selectedExtras) === JSON.stringify(action.payload.selectedExtras)
      );
      
      if (existingIndex >= 0) {
        const newItems = [...state.items];
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          quantity: newItems[existingIndex].quantity + action.payload.quantity
        };
        return { ...state, items: newItems };
      }
      
      return { ...state, items: [...state.items, { ...action.payload, cartId: Date.now() }] };
    }
    
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter(item => item.cartId !== action.payload) };
    
    case 'UPDATE_QUANTITY': {
      const newItems = state.items.map(item => 
        item.cartId === action.payload.cartId 
          ? { ...item, quantity: Math.max(1, action.payload.quantity) }
          : item
      );
      return { ...state, items: newItems };
    }
    
    case 'UPDATE_NOTE': {
      const newItems = state.items.map(item => 
        item.cartId === action.payload.cartId 
          ? { ...item, note: action.payload.note }
          : item
      );
      return { ...state, items: newItems };
    }
    
    case 'TOGGLE_CART':
      return { ...state, isOpen: !state.isOpen };
    
    case 'OPEN_CART':
      return { ...state, isOpen: true };
    
    case 'CLOSE_CART':
      return { ...state, isOpen: false };
    
    case 'SET_LOCATION':
      return { ...state, selectedLocation: action.payload };
    
    case 'SET_TIP':
      return { ...state, tip: action.payload };
    
    case 'APPLY_PROMO':
      return { ...state, promoCode: action.payload.code, promoDiscount: action.payload.discount };
    
    case 'REMOVE_PROMO':
      return { ...state, promoCode: null, promoDiscount: 0 };
    
    case 'CLEAR_CART':
      return { ...initialState, selectedLocation: state.selectedLocation };
    
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  
  const addItem = useCallback((item) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  }, []);
  
  const removeItem = useCallback((cartId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: cartId });
  }, []);
  
  const updateQuantity = useCallback((cartId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { cartId, quantity } });
  }, []);
  
  const updateNote = useCallback((cartId, note) => {
    dispatch({ type: 'UPDATE_NOTE', payload: { cartId, note } });
  }, []);
  
  const toggleCart = useCallback(() => {
    dispatch({ type: 'TOGGLE_CART' });
  }, []);
  
  const openCart = useCallback(() => {
    dispatch({ type: 'OPEN_CART' });
  }, []);
  
  const closeCart = useCallback(() => {
    dispatch({ type: 'CLOSE_CART' });
  }, []);
  
  const setLocation = useCallback((location) => {
    dispatch({ type: 'SET_LOCATION', payload: location });
  }, []);
  
  const setTip = useCallback((tip) => {
    dispatch({ type: 'SET_TIP', payload: tip });
  }, []);
  
  const applyPromo = useCallback((code, discount) => {
    dispatch({ type: 'APPLY_PROMO', payload: { code, discount } });
  }, []);
  
  const removePromo = useCallback(() => {
    dispatch({ type: 'REMOVE_PROMO' });
  }, []);
  
  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);
  
  // Calculate totals
  const itemCount = state.items.reduce((sum, item) => sum + item.quantity, 0);
  
  const subtotal = state.items.reduce((sum, item) => {
    let itemPrice = item.price;
    // Add variant prices
    if (item.selectedVariants) {
      Object.values(item.selectedVariants).forEach(variant => {
        itemPrice += variant.price || 0;
      });
    }
    // Add extras prices
    if (item.selectedExtras) {
      item.selectedExtras.forEach(extra => {
        itemPrice += extra.price || 0;
      });
    }
    return sum + (itemPrice * item.quantity);
  }, 0);
  
  const deliveryFee = state.selectedLocation 
    ? (subtotal >= state.selectedLocation.freeDeliveryMin ? 0 : state.selectedLocation.deliveryFee)
    : 0;
  
  const discount = state.promoDiscount > 0 ? (subtotal * state.promoDiscount / 100) : 0;
  
  const total = subtotal + deliveryFee + state.tip - discount;
  
  const freeDeliveryRemaining = state.selectedLocation 
    ? Math.max(0, state.selectedLocation.freeDeliveryMin - subtotal)
    : 0;
  
  return (
    <CartContext.Provider value={{
      ...state,
      addItem,
      removeItem,
      updateQuantity,
      updateNote,
      toggleCart,
      openCart,
      closeCart,
      setLocation,
      setTip,
      applyPromo,
      removePromo,
      clearCart,
      itemCount,
      subtotal,
      deliveryFee,
      discount,
      total,
      freeDeliveryRemaining,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
